import { Component, computed, effect, inject, model, OnInit, output, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddEditSOVCategoryComponent } from '@module-definition/category-group/add-edit-category/add-edit-category.component';
import { CategoryGroupService, SOVCategory, SOVCategoryGroupRow } from '@module-definition/category-group/common';
import {
  HorizontalSplitterComponent,
  PaneOptions,
} from '@module-definition/horizontal-splitter/horizontal-splitter.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoTextComponent } from '@shared/components/fields/cipo-text/cipo-text.component';
import { CipoFieldTypes, CipoListConfig, CipoTextControl } from '@shared/components/fields/common';
import {
  NestedList,
  NestedListAction,
  NestedListDropOtherListEvent,
  NestedListOptions,
} from '@shared/components/nested-list/common';
import { NestedListComponent } from '@shared/components/nested-list/nested-list.component';
import { ConfirmationDialog } from '@shared/dialogs/confirmation-dialog.component';
import { CipoFormModule } from '@shared/modules';
import { UtilsService } from '@shared/services';
import { UserService } from '@shared/services/user.service';
import { filter, map } from 'rxjs';

@Component({
  selector: 'cipo-category-group-view',
  imports: [
    CipoFormModule,
    NestedListComponent,
    CipoTextComponent,
    CdkDropListGroup,
    HorizontalSplitterComponent,
    CipoSelectComponent,
  ],
  templateUrl: './category-group-view.component.html',
  styles: [
    `
      .name-control {
        width: 300px;
      }

      /* Check this usage after implementing sov module settings in Angular */
      ::ng-deep .resize-container {
        height: 100%;
      }
    `,
  ],
})
export class CategoryGroupViewComponent implements OnInit {
  translate = inject(TranslateService);
  dialog = inject(MatDialog);
  userService = inject(UserService);
  categoryGroupService = inject(CategoryGroupService);
  utilsService = inject(UtilsService);

  nameControl = new CipoTextControl(null, {
    label: 'categoryGroup.nameColumn',
    required: true,
    appearanceFill: true,
  });

  initialCategoryGroup: SOVCategoryGroupRow;
  categoryGroup = model<SOVCategoryGroupRow>();
  categoryGroupList = signal<NestedList[]>(null);

  filterOtherCategories = signal<string[]>([]);
  otherCategories = signal<SOVCategory[]>(null);
  otherCategoriesArray = computed<NestedList[]>(() => {
    const filterOtherCategories = this.filterOtherCategories() ?? [];
    return this.convertToTree(
      this.otherCategories().filter(
        cat => !filterOtherCategories.length || filterOtherCategories.includes(cat.groupName),
      ),
    );
  });

  anythingChanged = model<boolean>(false);

  backToDataList = output();

  horizontalSplitterOptions = signal<PaneOptions>({
    minWidth: 0.4,
    maxWidth: 1,
    initialWidth: 1,
  });
  isOtherShowing = computed(() => this.horizontalSplitterOptions().initialWidth !== 1);

  topActions = computed<NestedListAction[]>(() => [
    {
      icon: this.isOtherShowing() ? 'arrow_menu_open' : 'arrow_menu_close',
      label: this.isOtherShowing() ? 'categoryGroup.hideOtherCategories' : 'categoryGroup.showOtherCategories',
      location: 'end',
      action: () => untracked(() => this.showOtherCategories()),
    },
    {
      icon: 'sort_by_alpha',
      label: 'categoryGroup.sortAlphabetically',
      location: 'end',
      children: [
        {
          icon: '',
          label: 'categoryGroup.sortByNumber',
          location: 'start',
          action: () => {
            this.categoryGroup.update(cg => {
              cg.categories = cg.categories.sort((a, b) => a.number.localeCompare(b.number));
              cg.categories = cg.categories.map((cat, index) => {
                cat.position = index;
                return cat;
              });
              return cg;
            });

            untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
            this.anythingChanged.set(true);
          },
        },
        {
          icon: '',
          label: 'categoryGroup.sortByName',
          location: 'start',
          action: () => {
            this.categoryGroup.update(cg => {
              cg.categories = cg.categories.sort((a, b) => a.name.localeCompare(b.name));
              cg.categories = cg.categories.map((cat, index) => {
                cat.position = index;
                return cat;
              });
              return cg;
            });

            untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
            this.anythingChanged.set(true);
          },
        },
      ],
    },
  ]);

  rowActions = signal<NestedListAction[]>([
    {
      icon: 'edit',
      label: 'actions.edit',
      location: 'end',
      action: (category: NestedList) => this.editCategory(category),
      condition: (category: NestedList) => !category.markedForDeletion,
    },
    {
      icon: 'delete',
      label: 'actions.delete',
      location: 'end',
      action: (category: NestedList) => this.deleteCategory(category.uniqueKey),
      condition: (category: NestedList) => !category.markedForDeletion && !category.isUsed,
    },
  ]);

  rowActionsOther = signal<NestedListAction[]>([
    {
      icon: 'arrow_left',
      label: 'actions.add',
      location: 'start',
      action: (category: NestedList) =>
        this.handleDropOtherList({
          previousIndex: this.otherCategories()
            .filter(item => item.visible)
            .findIndex(item => item.uniqueKey === category.uniqueKey),
          currentIndex: Math.max(...(this.categoryGroup().categories ?? []).map(item => item.position)) + 1,
          currentList: this.categoryGroupList(),
        }),
    },
  ]);

  otherCategoriesOptions = signal<NestedListOptions>({
    hideAddNewItem: true,
  });

  groupControl = new FormControl(null);
  groupControlData = signal<CipoListConfig>({
    label: 'categoryGroup.group',
    type: CipoFieldTypes.Select,
    multiple: true,
    appearanceFill: true,
  });

  constructor() {
    effect(() => {
      const categoryGroupList = this.categoryGroupList();
      if (categoryGroupList) {
        this.categoryGroup.update(cg => {
          cg.categories.map(cat => {
            const listItem = categoryGroupList.find(item => item.uniqueKey === cat.uniqueKey);
            cat.position = listItem ? listItem.position : cat.position;
            return cat;
          });
          return { ...cg };
        });
      }
    });
  }

  ngOnInit(): void {
    this.initialCategoryGroup = structuredClone(this.categoryGroup());
    untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
    this.nameControl.setValue(this.categoryGroup().name);
    this.nameControl.valueChanges.subscribe(() => {
      this.anythingChanged.set(true);
      this.categoryGroup.update(cg => {
        cg.name = this.nameControl.value as string;
        return cg;
      });
    });

    this.groupControl.valueChanges.subscribe(v => {
      this.filterOtherCategories.set(v);
    });
  }

  convertToTree(categories: SOVCategory[]): NestedList[] {
    const map = new Map<string, NestedList>();
    const roots: NestedList[] = [];

    // Pass 1: Create all tree nodes
    categories.forEach(category => {
      map.set(`${category.uniqueKey}`, {
        id: category.id,
        title: `${category.number} - ${category.name} ${category.groupName ? ' (' + category.groupName + ')' : ''}`,
        position: category.position,
        children: [],
        isUsed: category.isUsed,
        markedForDeletion: category.markedForDeletion,
        modified: category.modified,
        uniqueKey: category.uniqueKey,
        visible: category.visible ?? true,
      });
    });

    // Pass 2: Build parent-child relationships
    categories.forEach(category => {
      const node = map.get(`${category.uniqueKey}`)!;

      const parentKey = category.parentId
        ? `${categories.find(cat => cat.id === category.parentId)?.uniqueKey ?? ''}`
        : null;
      if (category.parentId && map.has(parentKey)) {
        // Has a parent - add to parent's children
        map.get(parentKey)!.children.push(node);
      } else {
        // No parent - this is a root node
        roots.push(node);
      }
    });

    return roots;
  }

  deleteCategory(categoryUniqueKey: string) {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'categoryGroup.deleteCategoryTitle',
          description: 'categoryGroup.deleteCategoryMessage',
        },
      })
      .afterClosed()
      .pipe(filter(val => !!val))
      .subscribe(() => {
        this.categoryGroup.update(cg => {
          // If new category, remove it. If existing, mark for deletion
          if (cg.categories.find(cat => cat.uniqueKey === categoryUniqueKey)?.id === 0) {
            cg.categories = cg.categories.filter(cat => cat.uniqueKey !== categoryUniqueKey);
            // Only if we have any other categories
            if (this.otherCategories()?.length) {
              this.otherCategories.update(list =>
                list.map(item => {
                  if (item.uniqueKey === categoryUniqueKey) {
                    return { ...item, visible: true };
                  }
                  return item;
                }),
              );
            }
          } else {
            cg.categories = cg.categories.map(cat => {
              if (cat.uniqueKey === categoryUniqueKey) {
                cat.markedForDeletion = true;
              }
              return cat;
            });
          }
          return cg;
        });
        untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
        this.anythingChanged.set(true);
      });
  }

  addEditCategoryInternal(newCategory: SOVCategory, elementPosition?: number) {
    this.categoryGroup.update(cg => {
      if (cg.categories.find(cat => cat.uniqueKey === newCategory.uniqueKey) == null) {
        // Add New Category
        cg.categories = [...cg.categories, newCategory];

        if (elementPosition !== null && elementPosition !== undefined) {
          // Get current index
          const currentIndex = cg.categories.findIndex(item => item.uniqueKey === newCategory.uniqueKey);
          // Move item into that position
          moveItemInArray(cg.categories, currentIndex, elementPosition ?? currentIndex);
          // Update positions
          cg.categories = cg.categories.map((item, index) => ({ ...item, position: index }));
        }
      } else {
        // update new category
        cg.categories = cg.categories.map(cat => {
          if (cat.uniqueKey === newCategory.uniqueKey) {
            newCategory.modified = true;
            return newCategory;
          }
          return cat;
        });
      }
      return cg;
    });

    untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
    this.anythingChanged.set(true);
  }

  addEditCategory(category: SOVCategory) {
    if (category) {
      this.dialog
        .open(AddEditSOVCategoryComponent, {
          panelClass: ['cipo-dialog', 'classic'],
          ...this.userService.getResponsiveDialogSize().xs,
          data: {
            category,
            addCategory: this.addEditCategoryInternal.bind(this),
            existingCategories: this.categoryGroup().categories,
          },
        })
        .afterClosed()
        .pipe(filter(val => !!val))
        .subscribe(newCategory => {
          this.addEditCategoryInternal(newCategory);
        });
    }
  }

  editCategory(category: NestedList) {
    const categoryToEdit = this.categoryGroup().categories.find(cat => cat.uniqueKey === category.uniqueKey);
    this.addEditCategory(categoryToEdit);
  }

  addNewCategory() {
    const newCategory: SOVCategory = {
      id: 0,
      rootCategoryId: 0,
      name: null,
      number: null,
      position: Math.max(...(this.categoryGroup().categories ?? []).map(item => item.position)) + 1,
      uniqueKey: crypto.randomUUID(),
      modified: true,
      isUsed: false,
    };

    this.addEditCategory(newCategory);
  }

  goBack() {
    if (this.anythingChanged()) {
      this.dialog
        .open(ConfirmationDialog, {
          data: {
            title: 'categoryGroup.goBackErrorTitle',
            description: 'categoryGroup.goBackErrorMessage',
          },
        })
        .afterClosed()
        .pipe(filter(val => !!val))
        .subscribe(() => {
          this.backToDataList.emit();
        });
    } else {
      this.backToDataList.emit();
    }
  }

  discardChanges() {
    this.categoryGroup.set(structuredClone(this.initialCategoryGroup));
    untracked(() => this.categoryGroupList.set(this.convertToTree(this.categoryGroup().categories)));
    this.nameControl.setValue(this.categoryGroup().name);
    if (this.otherCategories()?.length) {
      this.otherCategories.update(list => list.map(item => ({ ...item, visible: true })));
    }
  }

  showOtherCategories() {
    if (this.isOtherShowing()) {
      this.horizontalSplitterOptions.update(() => ({
        minWidth: 0.4,
        maxWidth: 0.7,
        initialWidth: 1,
      }));
    } else {
      this.categoryGroupService
        .getOtherCategories(this.categoryGroup().id)
        .pipe(map(category => category.map(cat => ({ ...cat, uniqueKey: crypto.randomUUID(), visible: true }))))
        .subscribe(categories => {
          this.groupControlData.update(data => ({
            ...data,
            options: this.utilsService
              .distinctBy('groupName', categories)
              .map(cat => ({ key: cat.groupName, value: cat.groupName })),
          }));

          this.otherCategories.set(
            categories
              .filter(
                cat =>
                  !this.categoryGroup().categories.some(
                    existingCat => cat.number === existingCat.number && cat.name === existingCat.name,
                  ),
              )
              .map(cat => ({
                ...cat,
                uniqueKey: crypto.randomUUID(),
              })),
          );
          this.horizontalSplitterOptions.update(() => ({
            minWidth: 0.4,
            maxWidth: 0.7,
            initialWidth: 0.7,
          }));
        });
    }
  }

  handleDropOtherList(event: NestedListDropOtherListEvent) {
    const isFirstList = this.categoryGroupList().every((item, i) => {
      let keys = Object.keys(item);
      return (
        keys.length === Object.keys(event.currentList[i]).length &&
        keys.every(key => event.currentList[i][key] === item[key])
      );
    });

    // If is dropping into the main list, then we need to add the category
    if (isFirstList) {
      const otherCategoriesList = this.otherCategories().filter(item => item.visible);

      const movedItem: SOVCategory = {
        id: 0,
        rootCategoryId: 0,
        name: otherCategoriesList[event.previousIndex].name,
        number: otherCategoriesList[event.previousIndex].number,
        uniqueKey: otherCategoriesList[event.previousIndex].uniqueKey,
        position: Math.max(...(this.categoryGroup().categories ?? []).map(item => item.position)) + 1,
        modified: true,
        isUsed: false,
      };

      this.addEditCategoryInternal(movedItem, event.currentIndex);
      this.otherCategories.update(list =>
        list.map(element => {
          if (element.uniqueKey === movedItem.uniqueKey) {
            return { ...element, visible: false };
          }
          return element;
        }),
      );
    } else {
      // Otherwise, we need to delete the category from the main list
      this.deleteCategory(this.categoryGroupList().filter(item => item.visible)[event.previousIndex].uniqueKey);
    }
  }
}
